import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  MapPin,
  Package,
  Trash2,
  Clock3,
  Route,
  Wallet,
  CircleDot,
  MoveDown,
  Truck,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "../../../auth/AuthContext";
import { downloadInvoicePDF } from "../../../utils/pdfHelpers";
import MapDisplay from "../../../LocationsModule/MapDisplay";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import PaymentCheckoutTest from "../../../components/CheckoutPage/PaymentCheckoutTest";
import { Order, useCreateOrderMutation } from "../../../orderApi/orderApi";
import GlobalSpinner from "../../../components/CustomReusables/GlobalSpinner";

const fallbackCenter = { lat: -13.9626, lng: 33.7741 };
const GOOGLE_MAPS_LIBRARIES: "places"[] = ["places"];
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

const cardsBorderRadii ="8px";

type ParcelSize = "small" | "medium" | "large" | "extra";

type DetailedItem = {
  name: string;
  qty: number;
  size?: ParcelSize | string;
};

type MinimalOrderItemShape = {
  title?: string;
  quantity?: number;
};

type RemoteOrder = Order & {
  fromAddress?: string;
  toAddress?: string;
  fromLocation?: { lat: number; lng: number };
  toLocation?: { lat: number; lng: number };
  deliveryInstructions?: string;
  phoneNumber?: string;
  email?: string;
  total?: number;
  pricingBreakdown?: {
    deliveryFee?: number;
    driverEarningTotal?: number;
    serviceFee?: number;
    total?: number;
    deliveryDistanceKm?: number;
    deliveryDurationMinutes?: number;
  };
};

type DirectDeliveryPricingBreakdown = {
  currency: string;
  productSubtotal: number;
  serviceFee: number;
  deliveryFee: number;
  discountAmount: number;
  gatewayFee: number;
  total: number;
  sellerCommissionTotal: number;
  sellerNetPayableTotal: number;
  driverEarningTotal: number;
  deliveryMargin: number;
  platformNetRevenue: number;
  deliveryDistanceKm: number;
  deliveryDurationMinutes: number;
  parcelSize: string;
  quantity: number;
  totalActualWeightKg: number;
  totalVolumetricWeightKg: number;
  billableWeightKg: number;
  bulkySurcharge: number;
};

type InfoPillProps = {
  label: string;
  value?: string | number;
  icon?: React.ReactNode;
  textColor: string;
  mutedText: string;
  bg: string;
  borderColor: string;
};

type PriceRowProps = {
  label: string;
  value: string;
  textColor: string;
  mutedText: string;
  isTotal?: boolean;
  accent?: string;
};

const cleanUserId = (value: any) => {
  const id = String(value || "").trim();

  if (!id) return "";
  if (id.toLowerCase() === "guest") return "";

  return id;
};

const getLoggedInUserId = (user: any) => {
  return cleanUserId(
    user?.uid || user?._id || localStorage.getItem("userId") || "",
  );
};

const toDetailedItem = (
  item: MinimalOrderItemShape,
  fallbackSize?: string,
): DetailedItem => ({
  name: item.title || "Parcel",
  qty: Number(item.quantity ?? 1) || 1,
  size: fallbackSize || "small",
});

const formatSizeLabel = (size?: string) => {
  if (!size) return "N/A";

  switch (String(size).toLowerCase()) {
    case "small":
      return "Small";
    case "medium":
      return "Medium";
    case "large":
      return "Large";
    case "extra":
    case "xlarge":
      return "Extra Large";
    default:
      return size;
  }
};

const normalizeParcelSize = (size?: string) => {
  const clean = String(size || "small")
    .trim()
    .toLowerCase();

  if (clean === "x-large" || clean === "extra large") return "extra";
  if (
    clean === "small" ||
    clean === "medium" ||
    clean === "large" ||
    clean === "extra"
  ) {
    return clean;
  }

  return "small";
};

const getSizeSurcharge = (size?: string) => {
  switch (normalizeParcelSize(size)) {
    case "small":
      return 0;
    case "medium":
      return 1000;
    case "large":
      return 2500;
    case "extra":
      return 5000;
    default:
      return 0;
  }
};

const roundMoney = (value: any) => Number(Number(value || 0).toFixed(2));

const parseDistanceKm = (value: any) => {
  if (typeof value === "number") return Number(value || 0);

  const raw = String(value || "").toLowerCase();

  if (!raw) return 0;

  const numeric = parseFloat(raw.replace(/[^0-9.]/g, "") || "0");

  if (raw.includes(" m") && !raw.includes("km")) {
    return Number((numeric / 1000).toFixed(2));
  }

  return numeric;
};

const parseDurationMinutes = (value: any) => {
  if (typeof value === "number") return Number(value || 0);

  const raw = String(value || "").toLowerCase();

  if (!raw) return 0;

  const hoursMatch = raw.match(/(\d+(?:\.\d+)?)\s*h/);
  const minsMatch = raw.match(/(\d+(?:\.\d+)?)\s*m/);

  const hours = hoursMatch ? Number(hoursMatch[1]) : 0;
  const minutes = minsMatch ? Number(minsMatch[1]) : 0;

  if (hours || minutes) return hours * 60 + minutes;

  return Number(parseFloat(raw.replace(/[^0-9.]/g, "") || "0"));
};

const calculateDirectDeliveryPricing = ({
  distanceKm,
  durationMinutes,
  parcelSize,
  quantity,
}: {
  distanceKm: number;
  durationMinutes: number;
  parcelSize: string;
  quantity: number;
}): DirectDeliveryPricingBreakdown => {
  const basePickupFee = 1500;
  const perKmRate = 450;
  const perMinuteRate = 40;
  const minDriverFee = 3000;
  const platformRate = 0.08;
  const minPlatformFee = 500;
  const longDistanceThresholdKm = 25;
  const longDistanceExtraPerKm = 150;

  const safeDistanceKm = Math.max(0, Number(distanceKm || 0));
  const safeDurationMinutes = Math.max(0, Number(durationMinutes || 0));
  const safeQuantity = Math.max(1, Number(quantity || 1));
  const safeSize = normalizeParcelSize(parcelSize);
  const sizeSurcharge = getSizeSurcharge(safeSize) * safeQuantity;

  const longDistanceKm = Math.max(0, safeDistanceKm - longDistanceThresholdKm);

  const driverFee = Math.max(
    minDriverFee,
    roundMoney(
      basePickupFee +
        safeDistanceKm * perKmRate +
        safeDurationMinutes * perMinuteRate +
        sizeSurcharge +
        longDistanceKm * longDistanceExtraPerKm,
    ),
  );

  const serviceFee = roundMoney(
    Math.max(minPlatformFee, driverFee * platformRate),
  );
  const total = roundMoney(driverFee + serviceFee);

  return {
    currency: "MWK",
    productSubtotal: 0,
    serviceFee,
    deliveryFee: driverFee,
    discountAmount: 0,
    gatewayFee: 0,
    total,
    sellerCommissionTotal: 0,
    sellerNetPayableTotal: 0,
    driverEarningTotal: driverFee,
    deliveryMargin: 0,
    platformNetRevenue: serviceFee,
    deliveryDistanceKm: roundMoney(safeDistanceKm),
    deliveryDurationMinutes: roundMoney(safeDurationMinutes),
    parcelSize: safeSize,
    quantity: safeQuantity,
    totalActualWeightKg: 0,
    totalVolumetricWeightKg: 0,
    billableWeightKg: 1,
    bulkySurcharge: sizeSurcharge,
  };
};

const InfoPill = ({
  label,
  value,
  icon,
  textColor,
  mutedText,
  bg,
  borderColor,
}: InfoPillProps) => {
  return (
    <Box
      flex="1 1 220px"
      minW={{ base: "100%", sm: "220px" }}
      bg={bg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius={cardsBorderRadii}
      px={4}
      py={3}
    >
      <HStack spacing={2} mb={1.5} align="center">
        {icon ? <Box flexShrink={0}>{icon}</Box> : null}
        <Text
          fontSize="xs"
          color={mutedText}
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.04em"
        >
          {label}
        </Text>
      </HStack>

      <Text
        color={textColor}
        fontWeight="700"
        fontSize="sm"
        wordBreak="break-word"
      >
        {value || "-"}
      </Text>
    </Box>
  );
};

const PriceRow = ({
  label,
  value,
  textColor,
  mutedText,
  isTotal = false,
  accent,
}: PriceRowProps) => {
  return (
    <Flex justify="space-between" align="center" py={isTotal ? 1.5 : 1}>
      <Text
        color={mutedText}
        fontSize="sm"
        fontWeight={isTotal ? "800" : "600"}
      >
        {label}
      </Text>

      <Text
        color={accent || textColor}
        fontSize={isTotal ? "lg" : "sm"}
        fontWeight={isTotal ? "900" : "800"}
        textAlign="right"
      >
        {value}
      </Text>
    </Flex>
  );
};

export const DeliveryCheckoutPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { theme } = useCustomTheme();

  const orderIdFromUrl = searchParams.get("orderId");
  const isWhatsAppMode = Boolean(orderIdFromUrl);

  const [remoteOrder, setRemoteOrder] = useState<RemoteOrder | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(() => {
    if (typeof window === "undefined") return true;

    // Mobile starts collapsed; tablet/desktop starts expanded.
    return window.matchMedia("(min-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(min-width: 768px)");

    const syncDeliveryDetailsVisibility = (event?: MediaQueryListEvent) => {
      setShowDeliveryDetails(
        typeof event?.matches === "boolean"
          ? event.matches
          : mediaQuery.matches,
      );
    };

    syncDeliveryDetailsVisibility();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncDeliveryDetailsVisibility);

      return () => {
        mediaQuery.removeEventListener("change", syncDeliveryDetailsVisibility);
      };
    }

    mediaQuery.addListener(syncDeliveryDetailsVisibility);

    return () => {
      mediaQuery.removeListener(syncDeliveryDetailsVisibility);
    };
  }, []);

  const {
    fromAddress: stateFromAddress,
    fromCoords: stateFromCoords,
    toAddress: stateToAddress,
    toCoords: stateToCoords,
    description,
    parcelSize,
    distance,
    duration,
    name,
    phone,
    email,
    deliveryInstructions: requestedDeliveryInstructions,
    detailedItems,
    items,

    // Optional PickDengu point data from RequestDelivery.
    pickupMode: statePickupMode,
    dropoffMode: stateDropoffMode,
    pickupWarehouseId: statePickupWarehouseId,
    pickupWarehouseName: statePickupWarehouseName,
    pickupWarehouseAddress: statePickupWarehouseAddress,
    dropoffWarehouseId: stateDropoffWarehouseId,
    dropoffWarehouseName: stateDropoffWarehouseName,
    dropoffWarehouseAddress: stateDropoffWarehouseAddress,
    dispatchWarehouseId: stateDispatchWarehouseId,
    sellerBusinessName: stateSellerBusinessName,
  } = (location.state as Record<string, any>) || {};

  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const directionsRequestIdRef = useRef(0);
  const [center, setCenter] = useState(fallbackCenter);
  const [routeDistanceKm, setRouteDistanceKm] = useState(
    parseDistanceKm(distance),
  );
  const [routeDurationMinutes, setRouteDurationMinutes] = useState(
    parseDurationMinutes(duration),
  );
  const [routeDistanceText, setRouteDistanceText] = useState(
    String(distance || ""),
  );
  const [routeDurationText, setRouteDurationText] = useState(
    String(duration || ""),
  );
  const [confirmed, setConfirmed] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<Order | null>(null);
  const [editableItems, setEditableItems] = useState<DetailedItem[]>([]);

  const [createOrderMutation] = useCreateOrderMutation();

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const buyerUserId = getLoggedInUserId(user);

  const isDark = theme.name === "dark";
  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    indigo: "#6f7cff",
    violet: "#b05cff",
  };

  const pageBg = theme.background;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const primaryButtonStyles = {
    color: theme.textOnPrimary,
    bgImage: theme.gradients?.primaryButton || theme.primary,
    boxShadow: theme.shadowSm,
    _hover: {
      bgImage: theme.gradients?.primaryButtonHover || theme.primaryHover,
      transform: "translateY(-1px)",
      boxShadow: theme.shadowMd,
    },
    _active: { transform: "translateY(0)" },
    borderRadius: cardsBorderRadii,
    transition: "all 0.2s ease",
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderIdFromUrl) return;

      try {
        setLoadingOrder(true);
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/orders/${orderIdFromUrl}`,
        );
        const fetched = response?.data?.data || response?.data;

        setRemoteOrder(fetched);
        setCreatedOrder(fetched);

        const breakdown = fetched?.pricingBreakdown || {};
        setRouteDistanceKm(Number(breakdown.deliveryDistanceKm || 0));
        setRouteDurationMinutes(Number(breakdown.deliveryDurationMinutes || 0));
        setRouteDistanceText(
          breakdown.deliveryDistanceKm
            ? `${Number(breakdown.deliveryDistanceKm).toFixed(1)} km`
            : "",
        );
        setRouteDurationText(
          breakdown.deliveryDurationMinutes
            ? `${Math.round(Number(breakdown.deliveryDurationMinutes))} mins`
            : "",
        );
      } catch (err: any) {
        toast({
          title: "Failed to load order",
          description:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Could not load delivery checkout.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingOrder(false);
      }
    };

    fetchOrder();
  }, [orderIdFromUrl, toast]);

  const fromAddress = remoteOrder?.fromAddress || stateFromAddress || "";
  const toAddress = remoteOrder?.toAddress || stateToAddress || "";
  const fromCoords = remoteOrder?.fromLocation || stateFromCoords;
  const toCoords = remoteOrder?.toLocation || stateToCoords;

  const resolvedPhone = remoteOrder?.phoneNumber || phone || "";
  const resolvedEmail = remoteOrder?.email || email || user?.email || "";
  const customerName = name || user?.displayName || "Customer";

  const initialDetailedItems: DetailedItem[] = useMemo(() => {
    if (remoteOrder?.items && remoteOrder.items.length > 0) {
      return remoteOrder.items.map((item) =>
        toDetailedItem(
          {
            title: item.title,
            quantity: item.quantity,
          },
          parcelSize || "small",
        ),
      );
    }

    if (Array.isArray(detailedItems) && detailedItems.length > 0) {
      return detailedItems.map((item: DetailedItem) => ({
        name: item?.name || "Parcel",
        qty: Number(item?.qty) || 1,
        size: item?.size || "small",
      }));
    }

    if (Array.isArray(items) && items.length > 0) {
      return items.map((item: any) => ({
        name: item?.name || item?.title || "Parcel",
        qty: Number(item?.qty ?? item?.quantity ?? 1) || 1,
        size: item?.size || parcelSize || "small",
      }));
    }

    if (description) {
      return [
        {
          name: description,
          qty: 1,
          size: parcelSize || "small",
        },
      ];
    }

    return [];
  }, [remoteOrder, detailedItems, items, description, parcelSize]);

  useEffect(() => {
    setEditableItems(initialDetailedItems);
  }, [initialDetailedItems]);

  const totalParcels = useMemo(
    () => editableItems.reduce((sum, item) => sum + (Number(item.qty) || 0), 0),
    [editableItems],
  );

  const largestParcelSize = useMemo(() => {
    const rank: Record<string, number> = {
      small: 1,
      medium: 2,
      large: 3,
      extra: 4,
    };

    return editableItems.reduce((current, item) => {
      const size = normalizeParcelSize(String(item.size || "small"));
      return rank[size] > rank[current] ? size : current;
    }, "small");
  }, [editableItems]);

  const deliveryPricingBreakdown = useMemo(() => {
    if (isWhatsAppMode && remoteOrder?.pricingBreakdown) {
      const existing = remoteOrder.pricingBreakdown;

      return {
        currency: "MWK",
        productSubtotal: 0,
        serviceFee: Number(existing.serviceFee || 0),
        deliveryFee: Number(
          existing.deliveryFee || existing.driverEarningTotal || 0,
        ),
        discountAmount: 0,
        gatewayFee: 0,
        total: Number(existing.total || remoteOrder.total || 0),
        sellerCommissionTotal: 0,
        sellerNetPayableTotal: 0,
        driverEarningTotal: Number(
          existing.driverEarningTotal || existing.deliveryFee || 0,
        ),
        deliveryMargin: 0,
        platformNetRevenue: Number(existing.serviceFee || 0),
        deliveryDistanceKm: Number(
          existing.deliveryDistanceKm || routeDistanceKm || 0,
        ),
        deliveryDurationMinutes: Number(
          existing.deliveryDurationMinutes || routeDurationMinutes || 0,
        ),
        parcelSize: largestParcelSize,
        quantity: Math.max(1, totalParcels),
        totalActualWeightKg: 0,
        totalVolumetricWeightKg: 0,
        billableWeightKg: 1,
        bulkySurcharge:
          getSizeSurcharge(largestParcelSize) * Math.max(1, totalParcels),
      };
    }

    return calculateDirectDeliveryPricing({
      distanceKm: routeDistanceKm,
      durationMinutes: routeDurationMinutes,
      parcelSize: largestParcelSize,
      quantity: Math.max(1, totalParcels),
    });
  }, [
    isWhatsAppMode,
    remoteOrder,
    routeDistanceKm,
    routeDurationMinutes,
    largestParcelSize,
    totalParcels,
  ]);

  const deliveryInstructionsText = useMemo(() => {
    if (isWhatsAppMode && remoteOrder?.deliveryInstructions) {
      return remoteOrder.deliveryInstructions;
    }

    const parcelDetails =
      editableItems.length === 0
        ? String(description || "").trim()
        : editableItems
            .map(
              (item, index) =>
                `${index + 1}. ${item.name} - Qty: ${item.qty}${
                  item.size
                    ? ` - Size: ${formatSizeLabel(String(item.size))}`
                    : ""
                }`,
            )
            .join("\n");

    const customerInstructions = String(
      requestedDeliveryInstructions || "",
    ).trim();

    return [
      parcelDetails,
      customerInstructions
        ? `Customer delivery instructions: ${customerInstructions}`
        : "",
    ]
      .filter(Boolean)
      .join("\n\n");
  }, [
    isWhatsAppMode,
    remoteOrder,
    editableItems,
    description,
    requestedDeliveryInstructions,
  ]);

  const fetchDirections = useCallback(async () => {
    if (
      !isLoaded ||
      !fromCoords ||
      !toCoords ||
      typeof window === "undefined"
    ) {
      return;
    }

    const mapsApi = window.google?.maps as any;

    if (!mapsApi) {
      return;
    }

    const requestId = ++directionsRequestIdRef.current;

    try {
      /*
       * Load the routes library on demand when available. This is safer when
       * returning to the page because the base Maps script may be present
       * before DirectionsService has finished loading.
       */
      let routesLibrary: any = null;

      if (typeof mapsApi.importLibrary === "function") {
        try {
          routesLibrary = await mapsApi.importLibrary("routes");
        } catch (libraryError) {
          console.warn(
            "[DeliveryCheckout] Could not load the Google Maps routes library. Falling back to the global API.",
            libraryError,
          );
        }
      }

      if (requestId !== directionsRequestIdRef.current) {
        return;
      }

      const DirectionsServiceConstructor =
        routesLibrary?.DirectionsService || mapsApi.DirectionsService;

      if (typeof DirectionsServiceConstructor !== "function") {
        console.warn(
          "[DeliveryCheckout] Google Maps DirectionsService is not available yet.",
        );
        return;
      }

      const travelMode =
        routesLibrary?.TravelMode?.DRIVING ||
        mapsApi.TravelMode?.DRIVING ||
        "DRIVING";

      const directionsService = new DirectionsServiceConstructor();

      directionsService.route(
        {
          origin: fromCoords,
          destination: toCoords,
          travelMode,
        },
        (result: google.maps.DirectionsResult | null, status: string) => {
          if (requestId !== directionsRequestIdRef.current) {
            return;
          }

          if (status !== "OK" || !result) {
            console.warn(
              "[DeliveryCheckout] Google Maps directions request was not completed.",
              status,
            );
            return;
          }

          setDirections(result);

          const leg = result.routes[0]?.legs?.[0];

          if (!leg) {
            return;
          }

          setCenter({
            lat: leg.start_location.lat(),
            lng: leg.start_location.lng(),
          });

          const distanceValueKm = Number(
            ((leg.distance?.value || 0) / 1000).toFixed(2),
          );
          const durationValueMinutes = Number(
            ((leg.duration?.value || 0) / 60).toFixed(2),
          );

          setRouteDistanceKm(distanceValueKm);
          setRouteDurationMinutes(durationValueMinutes);
          setRouteDistanceText(leg.distance?.text || `${distanceValueKm} km`);
          setRouteDurationText(
            leg.duration?.text || `${Math.round(durationValueMinutes)} mins`,
          );
        },
      );
    } catch (error) {
      if (requestId !== directionsRequestIdRef.current) {
        return;
      }

      /*
       * Do not crash the checkout page when the Maps API is still restoring
       * after browser navigation. Existing route values remain available and
       * the customer can safely retry by revisiting the page.
       */
      console.error(
        "[DeliveryCheckout] Failed to initialize Google Maps directions.",
        error,
      );
    }
  }, [isLoaded, fromCoords, toCoords]);

  useEffect(() => {
    void fetchDirections();

    return () => {
      /*
       * Invalidates any delayed importLibrary/route callback after navigation
       * or a React StrictMode effect cleanup.
       */
      directionsRequestIdRef.current += 1;
    };
  }, [fetchDirections]);

  const handleRemoveParcel = (indexToRemove: number) => {
    if (isWhatsAppMode) {
      toast({
        title: "Editing disabled",
        description: "WhatsApp checkout uses the already created order.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setEditableItems((prev) => {
      const updated = prev.filter((_, index) => index !== indexToRemove);

      if (updated.length === 0) {
        toast({
          title: "All parcels removed",
          description: "Add at least one parcel before continuing to payment.",
          status: "warning",
          duration: 3500,
          isClosable: true,
        });
      }

      return updated;
    });
  };

  const createDeliveryOrder = async (): Promise<string | null> => {
    if (isWhatsAppMode) {
      if (!orderIdFromUrl) {
        toast({
          title: "Missing order ID",
          description: "Could not continue with payment.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      setConfirmed(true);

      toast({
        title: "Using existing WhatsApp order",
        description: "Proceeding to payment flow.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      return orderIdFromUrl;
    }

    try {
      if (!buyerUserId) {
        toast({
          title: "Please sign in",
          description: "You must be signed in to request a delivery.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      if (!fromAddress || !toAddress) {
        toast({
          title: "Missing delivery addresses",
          description: "Pickup and dropoff addresses are required.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      if (!fromCoords || !toCoords) {
        toast({
          title: "Missing delivery coordinates",
          description:
            "Pickup and dropoff map coordinates are required for dispatch.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      if (!routeDistanceKm || routeDistanceKm <= 0) {
        toast({
          title: "Route distance unavailable",
          description: "Please wait for the route to calculate before paying.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      if (editableItems.length === 0) {
        toast({
          title: "No parcels available",
          description: "Please keep at least one parcel before payment.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      const directWarehouseId = `direct:${buyerUserId}`;

      // Own-address deliveries keep the existing direct:* behavior.
      // PickDengu-point pickup deliveries use the selected pickup warehouse so
      // dispatch remains attached to that warehouse exactly like mobile.
      const resolvedDispatchWarehouseId =
        String(
          stateDispatchWarehouseId || statePickupWarehouseId || "",
        ).trim() || directWarehouseId;
      const resolvedPickupWarehouseName =
        String(statePickupWarehouseName || "").trim() || "Direct Delivery";
      const resolvedPickupWarehouseAddress =
        String(statePickupWarehouseAddress || "").trim() || fromAddress;
      const resolvedSellerBusinessName =
        String(stateSellerBusinessName || "").trim() ||
        resolvedPickupWarehouseName;

      const now = Date.now();

      const orderPayload: Omit<Order, "_id"> & {
        name?: string;
        ownerUserId?: string;
        sellerUserId?: string;
        sellerName?: string;
        checkoutMode?: string;
        pickupAddress?: string;
        pickupWarehouseId?: string;
        pickupWarehouseName?: string;
        pickupWarehouseAddress?: string;
        dispatchWarehouseId?: string;
        pricingInput?: Record<string, any>;
        pricingBreakdown?: DirectDeliveryPricingBreakdown;
      } = {
        userId: buyerUserId,

        ownerUserId: buyerUserId,
        sellerUserId: "",
        sellerName: "Direct Delivery",

        email: resolvedEmail || "",
        phoneNumber: resolvedPhone || "",
        name: customerName,

        category: "delivery",
        checkoutMode: "delivery",

        items: editableItems.map((item, index) => {
          const quantity = Number(item.qty) || 1;
          const unitPrice =
            totalParcels > 0
              ? roundMoney(deliveryPricingBreakdown.deliveryFee / totalParcels)
              : Number(deliveryPricingBreakdown.deliveryFee || 0);

          return {
            _id: `delivery-${now}-${index}`,
            productId: `delivery-${now}-${index}`,
            sellerUserId: "",
            ownerId: buyerUserId,
            dispatchWarehouseId: resolvedDispatchWarehouseId,
            sellerBusinessName: resolvedSellerBusinessName,
            title: item.name || "Delivery Parcel",
            description: `Size: ${formatSizeLabel(
              String(item.size || "small"),
            )} | Sender: ${customerName}`,
            category: "delivery",
            imageUrl: "",
            price: unitPrice,
            quantity,
            selectedVariants: [],
            variantSummary: `Size: ${formatSizeLabel(String(item.size || "small"))}`,
            isPhysical: true,
            weightKg: 0,
            lengthCm: 0,
            widthCm: 0,
            heightCm: 0,
            bulky:
              normalizeParcelSize(String(item.size || "small")) === "extra",
            meta: {
              parcelSize: normalizeParcelSize(String(item.size || "small")),
              pickupAddress: fromAddress,
              dropoffAddress: toAddress,
              senderName: customerName,
              senderPhone: resolvedPhone || "",
              distanceKm: routeDistanceKm,
              durationMinutes: routeDurationMinutes,
              pickupMode: statePickupMode || "address",
              dropoffMode: stateDropoffMode || "address",
              pickupWarehouseId: statePickupWarehouseId || "",
              pickupWarehouseName: statePickupWarehouseName || "",
              dropoffWarehouseId: stateDropoffWarehouseId || "",
              dropoffWarehouseName: stateDropoffWarehouseName || "",
              dropoffWarehouseAddress: stateDropoffWarehouseAddress || "",
            },
          };
        }) as any,

        total: Number(deliveryPricingBreakdown.total || 0),
        currency: "MWK",

        fromAddress,
        toAddress,
        fromLocation: {
          lat: fromCoords.lat,
          lng: fromCoords.lng,
        },
        toLocation: {
          lat: toCoords.lat,
          lng: toCoords.lng,
        },

        pickupAddress: fromAddress,
        pickupWarehouseId: resolvedDispatchWarehouseId,
        pickupWarehouseName: resolvedPickupWarehouseName,
        pickupWarehouseAddress: resolvedPickupWarehouseAddress,
        dispatchWarehouseId: resolvedDispatchWarehouseId,

        deliveryInstructions: deliveryInstructionsText,

        pricingInput: {
          distanceKm: routeDistanceKm,
          durationMinutes: routeDurationMinutes,
          parcelSize: largestParcelSize,
          quantity: Math.max(1, totalParcels),
          priority: "standard",
        },

        pricingBreakdown: deliveryPricingBreakdown,

        payment: {
          cardNumber: "",
          cardHolder: "",
          expiryDate: "",
          cvv: "",
          method: "",
          provider: "",
          status: "pending",
        } as any,
        status: "pending",
      };

      const response = await createOrderMutation(orderPayload).unwrap();
      const created = response;
      const createdId = created?._id || null;

      if (!createdId) {
        toast({
          title: "Order created but ID missing",
          description: "The server response did not include an order ID.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return null;
      }

      setCreatedOrder(created);
      setConfirmed(true);

      toast({
        title: "Delivery order created",
        description: "Proceeding to payment flow.",
        status: "success",
        duration: 3500,
        isClosable: true,
      });

      return createdId;
    } catch (err: any) {
      toast({
        status: "error",
        title: "Failed to create delivery order",
        description:
          err?.data?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Please try again.",
        duration: 5000,
        isClosable: true,
      });

      return null;
    }
  };

  if (!isLoaded || loadingOrder) {
    return (
      <Flex
        minH="280px"
        justify="center"
        align="center"
        direction="column"
        gap={3}
        bg={pageBg}
      >
        <GlobalSpinner />
        <Text color={mutedText}>
          {loadingOrder ? "Loading order..." : "Loading maps..."}
        </Text>
      </Flex>
    );
  }

  if (!fromAddress) {
    return (
      <Box w="100%" bg={pageBg} py={4} px={4} mx="auto">
        <Text color={textColor}>No delivery details provided.</Text>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      bg={pageBg}
      py={{ base: 3, md: 4 }}
      px={{ base: 3, md: 4 }}
      mx="auto"
      color={textColor}
    >
      <Heading color={textColor} size="lg" mb={4}>
        Delivery Checkout
      </Heading>

      <Flex
        gap={6}
        flexDirection={{ base: "column", xl: "row" }}
        align="stretch"
      >
        <Box w="100%" minW={0} flex="1">
          <Box mb={4}>
            <Button
              onClick={() => setShowDeliveryDetails((prev) => !prev)}
              w="100%"
              justifyContent="space-between"
              bg={cardBg}
              color={textColor}
              border="1px solid"
              borderColor={borderStrong}
              borderRadius={cardsBorderRadii}
              px={4}
              py={6}
              boxShadow={theme.shadowSm}
              _hover={{
                bg: softBg,
              }}
              _active={{
                bg: softBg,
              }}
              rightIcon={
                showDeliveryDetails ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )
              }
            >
              {showDeliveryDetails
                ? "Hide delivery details"
                : "Show delivery details"}
            </Button>
          </Box>

          {showDeliveryDetails && (
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius={cardsBorderRadii}
              boxShadow={theme.shadowSm}
              overflow="hidden"
              mb={4}
            >
              <Box
                px={{ base: 4, md: 5 }}
                py={{ base: 4, md: 5 }}
                bg={
                  isDark
                    ? "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)"
                    : "linear-gradient(180deg, rgba(39,183,255,0.05) 0%, rgba(255,255,255,0.7) 100%)"
                }
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                <Flex
                  justify="space-between"
                  align={{ base: "flex-start", md: "center" }}
                  gap={3}
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Box>
                    <HStack spacing={2} mb={1}>
                      <Truck size={18} color={brand.electricBlue} />
                      <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="0.08em"
                        color={mutedText}
                        fontWeight="800"
                      >
                        A → B Delivery
                      </Text>
                    </HStack>

                    <Heading size="md" color={textColor}>
                      Review your trip and parcels
                    </Heading>

                    <Text color={mutedText} fontSize="sm" mt={1}>
                      Priced by real route kilometres, route time, and parcel
                      size.
                    </Text>
                  </Box>

                  <HStack spacing={2} flexWrap="wrap">
                    {isWhatsAppMode ? (
                      <Badge
                        px={3}
                        py={1.5}
                        borderRadius={cardsBorderRadii}
                        bg={`${brand.electricBlue}14`}
                        color={textColor}
                        border="1px solid"
                        borderColor={`${brand.electricBlue}35`}
                      >
                        WhatsApp Checkout
                      </Badge>
                    ) : null}

                    <Badge
                      px={3}
                      py={1.5}
                      borderRadius={cardsBorderRadii}
                      bg={`${brand.electricBlue}14`}
                      color={textColor}
                      border="1px solid"
                      borderColor={`${brand.electricBlue}35`}
                    >
                      {totalParcels} total parcel{totalParcels === 1 ? "" : "s"}
                    </Badge>
                  </HStack>
                </Flex>
              </Box>

              <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
                <Flex wrap="wrap" gap={3} mb={4}>
                  <InfoPill
                    label="Name"
                    value={customerName}
                    textColor={textColor}
                    mutedText={mutedText}
                    bg={softBg}
                    borderColor={borderColor}
                  />
                  <InfoPill
                    label="Phone"
                    value={resolvedPhone}
                    textColor={textColor}
                    mutedText={mutedText}
                    bg={softBg}
                    borderColor={borderColor}
                  />
                  {resolvedEmail ? (
                    <InfoPill
                      label="Email"
                      value={resolvedEmail}
                      textColor={textColor}
                      mutedText={mutedText}
                      bg={softBg}
                      borderColor={borderColor}
                    />
                  ) : null}
                </Flex>

                <Flex gap={3} direction="column" mb={4}>
                  <Box
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius={cardsBorderRadii}
                    px={{ base: 3.5, md: 4 }}
                    py={3.5}
                    bg={
                      isDark
                        ? "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%)"
                        : "linear-gradient(180deg, rgba(39,183,255,0.05) 0%, rgba(255,255,255,0.95) 100%)"
                    }
                    boxShadow={theme.shadowSm}
                  >
                    <Flex align="stretch" gap={3}>
                      <Flex
                        direction="column"
                        align="center"
                        pt={1}
                        flexShrink={0}
                      >
                        <Flex
                          w="32px"
                          h="32px"
                          borderRadius={cardsBorderRadii}
                          align="center"
                          justify="center"
                          bg={`${brand.electricBlue}14`}
                          border="1px solid"
                          borderColor={`${brand.electricBlue}35`}
                        >
                          <CircleDot size={15} color={brand.electricBlue} />
                        </Flex>

                        <Flex
                          direction="column"
                          align="center"
                          justify="center"
                          py={1.5}
                          minH="28px"
                        >
                          <Box
                            w="2px"
                            h="12px"
                            bg={
                              isDark
                                ? "rgba(255,255,255,0.12)"
                                : "rgba(42,44,59,0.12)"
                            }
                            borderRadius={cardsBorderRadii}
                          />
                          <Flex my={1} color={brand.indigo}>
                            <MoveDown size={14} />
                          </Flex>
                          <Box
                            w="2px"
                            h="12px"
                            bg={
                              isDark
                                ? "rgba(255,255,255,0.12)"
                                : "rgba(42,44,59,0.12)"
                            }
                            borderRadius={cardsBorderRadii}
                          />
                        </Flex>

                        <Flex
                          w="32px"
                          h="32px"
                          borderRadius={cardsBorderRadii}
                          align="center"
                          justify="center"
                          bg={`${brand.indigo}14`}
                          border="1px solid"
                          borderColor={`${brand.indigo}35`}
                        >
                          <MapPin size={15} color={brand.indigo} />
                        </Flex>
                      </Flex>

                      <Flex direction="column" gap={3} flex="1" minW={0}>
                        <Box
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          px={3}
                          py={2.5}
                          bg={
                            isDark
                              ? "rgba(255,255,255,0.025)"
                              : "rgba(255,255,255,0.88)"
                          }
                        >
                          <Text
                            fontSize="11px"
                            textTransform="uppercase"
                            letterSpacing="0.08em"
                            color={mutedText}
                            fontWeight="800"
                            mb={1}
                          >
                            Pickup
                          </Text>
                          <Text
                            color={textColor}
                            fontWeight="700"
                            fontSize="sm"
                            lineHeight="1.35"
                            wordBreak="break-word"
                          >
                            {fromAddress}
                          </Text>
                        </Box>

                        <Box
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          px={3}
                          py={2.5}
                          bg={
                            isDark
                              ? "rgba(255,255,255,0.025)"
                              : "rgba(255,255,255,0.88)"
                          }
                        >
                          <Text
                            fontSize="11px"
                            textTransform="uppercase"
                            letterSpacing="0.08em"
                            color={mutedText}
                            fontWeight="800"
                            mb={1}
                          >
                            Dropoff
                          </Text>
                          <Text
                            color={textColor}
                            fontWeight="700"
                            fontSize="sm"
                            lineHeight="1.35"
                            wordBreak="break-word"
                          >
                            {toAddress}
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                  </Box>
                </Flex>

                <Flex wrap="wrap" gap={3} mb={4}>
                  <InfoPill
                    label="Kilometres"
                    value={
                      routeDistanceText || `${routeDistanceKm.toFixed(1)} km`
                    }
                    icon={<Route size={15} color={brand.electricBlue} />}
                    textColor={textColor}
                    mutedText={mutedText}
                    bg={softBg}
                    borderColor={borderColor}
                  />

                  <InfoPill
                    label="Duration"
                    value={
                      routeDurationText ||
                      `${Math.round(routeDurationMinutes)} mins`
                    }
                    icon={<Clock3 size={15} color={brand.indigo} />}
                    textColor={textColor}
                    mutedText={mutedText}
                    bg={softBg}
                    borderColor={borderColor}
                  />
                </Flex>

                <Flex
                  justify="space-between"
                  align={{ base: "flex-start", md: "center" }}
                  gap={2}
                  flexDirection={{ base: "column", md: "row" }}
                  mb={3}
                >
                  <HStack spacing={2}>
                    <Package size={17} color={brand.violet} />
                    <Heading size="sm" color={textColor}>
                      Parcels
                    </Heading>
                  </HStack>

                  <Text fontSize="xs" color={mutedText} fontWeight="700">
                    {isWhatsAppMode
                      ? "Loaded from your WhatsApp order"
                      : "Remove anything you don’t want to send"}
                  </Text>
                </Flex>

                <VStack spacing={2.5} align="stretch">
                  {editableItems.length > 0 ? (
                    editableItems.map((item, index) => (
                      <Box
                        key={`${item.name}-${index}`}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius={cardsBorderRadii}
                        px={{ base: 3, md: 3.5 }}
                        py={2.5}
                        bg={isDark ? "rgba(255,255,255,0.02)" : "white"}
                        transition="all 0.2s ease"
                      >
                        <Flex align="center" justify="space-between" gap={3}>
                          <Flex gap={2.5} flex="1" minW={0} align="center">
                            <Flex
                              w="34px"
                              h="34px"
                              borderRadius={cardsBorderRadii}
                              bg={`${brand.violet}14`}
                              border="1px solid"
                              borderColor={`${brand.violet}35`}
                              align="center"
                              justify="center"
                              flexShrink={0}
                            >
                              <Package size={15} color={brand.violet} />
                            </Flex>

                            <Flex
                              flex="1"
                              minW={0}
                              align={{ base: "flex-start", sm: "center" }}
                              justify="space-between"
                              gap={2}
                              direction={{ base: "column", sm: "row" }}
                            >
                              <Text
                                color={textColor}
                                fontWeight="700"
                                fontSize="sm"
                                wordBreak="break-word"
                                minW={0}
                              >
                                {item.name}
                              </Text>

                              <HStack spacing={2} flexWrap="wrap">
                                <Badge
                                  px={2}
                                  py={0.5}
                                  borderRadius={cardsBorderRadii}
                                  bg={
                                    isDark ? "rgba(255,255,255,0.05)" : softBg
                                  }
                                  color={textColor}
                                  border="1px solid"
                                  borderColor={borderColor}
                                  fontSize="0.7rem"
                                >
                                  Qty {item.qty}
                                </Badge>

                                <Badge
                                  px={2}
                                  py={0.5}
                                  borderRadius={cardsBorderRadii}
                                  bg={`${brand.violet}14`}
                                  color={textColor}
                                  border="1px solid"
                                  borderColor={`${brand.violet}35`}
                                  fontSize="0.7rem"
                                >
                                  {formatSizeLabel(
                                    String(item.size || "small"),
                                  )}
                                </Badge>
                              </HStack>
                            </Flex>
                          </Flex>

                          {!isWhatsAppMode && (
                            <IconButton
                              aria-label={`Remove ${item.name}`}
                              icon={<Trash2 size={15} />}
                              size="sm"
                              variant="ghost"
                              color={mutedText}
                              minW="34px"
                              h="34px"
                              _hover={{
                                bg: isDark
                                  ? "rgba(255,255,255,0.06)"
                                  : "rgba(0,0,0,0.04)",
                                color: brand.violet,
                              }}
                              onClick={() => handleRemoveParcel(index)}
                            />
                          )}
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Box
                      border="1px dashed"
                      borderColor={borderColor}
                      borderRadius={cardsBorderRadii}
                      px={4}
                      py={5}
                      textAlign="center"
                      bg={softBg}
                    >
                      <Text color={mutedText} fontSize="sm" fontWeight="600">
                        No parcels selected.
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            </Box>
          )}

          <Box
            border="1px solid"
            borderColor={borderColor}
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            boxShadow={theme.shadowSm}
            overflow="hidden"
          >
            <PaymentCheckoutTest
              amount={Number(deliveryPricingBreakdown.total || 0)}
              onCreateOrder={createDeliveryOrder}
              shouldClearCart={false}
            />
          </Box>

          {confirmed && createdOrder && (
            <Button
              mt={4}
              {...primaryButtonStyles}
              onClick={() =>
                downloadInvoicePDF({
                  delivery: {
                    _id: createdOrder?._id,
                    pickup: { address: fromAddress, ...(fromCoords || {}) },
                    dropoff: { address: toAddress, ...(toCoords || {}) },
                    items: editableItems.map((item) => ({
                      name: item.name,
                      qty: item.qty,
                      size: item.size,
                    })),
                    fee: Number(deliveryPricingBreakdown.total || 0),
                  },
                  user,
                  distance:
                    routeDistanceText || `${routeDistanceKm.toFixed(1)} km`,
                  duration:
                    routeDurationText ||
                    `${Math.round(routeDurationMinutes)} mins`,
                })
              }
              isDisabled={editableItems.length === 0}
            >
              Download Invoice
            </Button>
          )}
        </Box>

        <Box w="100%" maxW={{ base: "100%", xl: "390px" }}>
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius={cardsBorderRadii}
            boxShadow={theme.shadowSm}
            overflow="hidden"
            position="sticky"
            top="16px"
          >
            <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
              <Flex align="center" justify="space-between" mb={4}>
                <Box>
                  <Heading size="sm" color={textColor}>
                    Payment summary
                  </Heading>
                  <Text fontSize="sm" color={mutedText} mt={1}>
                    Driver delivery fee plus platform processing fee
                  </Text>
                </Box>
                <Wallet size={18} color={brand.electricBlue} />
              </Flex>

              <Box
                border="1px solid"
                borderColor={borderColor}
                borderRadius={cardsBorderRadii}
                bg={softBg}
                px={4}
                py={4}
                mb={4}
              >
                <PriceRow
                  label="Driver delivery fee"
                  value={`MWK ${Number(
                    deliveryPricingBreakdown.driverEarningTotal || 0,
                  ).toLocaleString()}`}
                  textColor={textColor}
                  mutedText={mutedText}
                />

                <PriceRow
                  label="Platform processing fee"
                  value={`MWK ${Number(
                    deliveryPricingBreakdown.serviceFee || 0,
                  ).toLocaleString()}`}
                  textColor={textColor}
                  mutedText={mutedText}
                />

                <Divider borderColor={borderColor} my={2} />

                <PriceRow
                  label="Total to pay"
                  value={`MWK ${Number(
                    deliveryPricingBreakdown.total || 0,
                  ).toLocaleString()}`}
                  textColor={textColor}
                  mutedText={mutedText}
                  isTotal
                  accent={brand.electricBlue}
                />
              </Box>

              <HStack
                align="start"
                spacing={2}
                border="1px solid"
                borderColor={borderColor}
                bg={
                  isDark ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.78)"
                }
                borderRadius={cardsBorderRadii}
                p={3}
              >
                <ShieldCheck size={17} color={brand.indigo} />
                <Text fontSize="sm" color={mutedText} fontWeight="600">
                  Driver payout becomes eligible after payment is confirmed and
                  delivery is completed.
                </Text>
              </HStack>
            </Box>

            {directions && (
              <Box px={{ base: 4, md: 5 }} pb={{ base: 4, md: 5 }}>
                <Box
                  borderRadius={cardsBorderRadii}
                  overflow="hidden"
                  border="1px solid"
                  borderColor={borderColor}
                  bg={isDark ? "rgba(255,255,255,0.02)" : "white"}
                >
                  <MapDisplay
                    height="340px"
                    directions={directions}
                    center={center}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default DeliveryCheckoutPage;
